import { useState, useMemo } from 'react';
import { X, CalendarDays, Loader2, FileText, Search, ChevronDown } from 'lucide-react';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';
import { ContactsTable, DirectoryRefresh } from '@/components/ContactsTable';
import { useTimezone } from '@/components/TimezoneContext';
import { exportLeadsToPdf } from '@/utils/pdfExport';
import { PageIntro, count } from '@/workspace/ui';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// ── date range helpers (same logic as AutomationRuns) ─────────────────────────
const DATE_OPTIONS = [
  { value: 'all',    label: 'All time' },
  { value: 'today',  label: 'Today' },
  { value: '7d',     label: 'Last 7 days' },
  { value: '30d',    label: 'Last 30 days' },
  { value: '90d',    label: 'Last 90 days' },
  { value: 'custom', label: 'Custom range…' },
];

function localDateStr(d, timezone) {
  if (timezone) {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(d);
    const value = type => parts.find(p => p.type === type)?.value;
    return `${value('year')}-${value('month')}-${value('day')}`;
  }
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}


function dateLabel(range, customRange) {
  if (range === 'all' || !range) return null;
  if (range === 'custom' && customRange?.from) {
    const opts = { month: 'short', day: 'numeric', year: 'numeric' };
    const from = customRange.from.toLocaleDateString('en-US', opts);
    if (!customRange.to || customRange.from.toDateString() === customRange.to.toDateString()) return from;
    return `${from} – ${customRange.to.toLocaleDateString('en-US', opts)}`;
  }
  return DATE_OPTIONS.find(o => o.value === range)?.label || null;
}

function passesDate(contact, range, customRange, todayStr, timezone) {
  if (range === 'all') return true;
  const d = new Date(contact.updated_at);
  if (!contact.updated_at || Number.isNaN(d.getTime())) return false;
  const now = Date.now();
  if (range === 'today') {
    const cd = localDateStr(d, timezone);
    return cd === todayStr;
  }
  if (range === '7d')  return d >= new Date(now - 7  * 864e5);
  if (range === '30d') return d >= new Date(now - 30 * 864e5);
  if (range === '90d') return d >= new Date(now - 90 * 864e5);
  if (range === 'custom' && customRange?.from) {
    const from = customRange.from;
    const to   = customRange.to || customRange.from;
    // Calendar choices are local day values; timestamps use the display timezone.
    const dStr    = localDateStr(d, timezone);
    const fromStr = localDateStr(from);
    const toStr   = localDateStr(to);
    return dStr >= fromStr && dStr <= toStr;
  }
  return true;
}

export default function LeadsPage({ embedded = false, contacts, loading, initialLoad, contactsError, error: loadError, stats, onRefresh, onSelectContact, onDeleteContact, onBulkDelete }) {
  const error = contactsError || loadError;
  const { timezone, todayString } = useTimezone();
  const [search,       setSearch]       = useState('');
  const [srcFilter,    setSrc]          = useState('all');
  const [dateRange,    setDateRange]    = useState('all');
  const [customRange,  setCustomRange]  = useState(null);
  const [draftRange,   setDraftRange]   = useState(null);
  const [calOpen,      setCalOpen]      = useState(false);
  const [exporting,    setExporting]    = useState(false);
  const [selectedIds,  setSelectedIds]  = useState(new Set()); // track checkbox selection from table

  const handleCopyScript = () =>
    navigator.clipboard.writeText(`<script src="${BACKEND_URL}/api/shumard.js"></script>`)
      .then(() => toast.success('Script copied!'));

  const handleDateRangeChange = (val) => {
    if (val === 'custom') { setDraftRange(customRange); setCalOpen(true); return; }
    setDateRange(val);
    setCustomRange(null);
  };

  const todayStr = todayString();

  const identified = useMemo(() => contacts.filter(c => c.email || c.phone || c.name), [contacts]);
  const sources    = useMemo(() => {
    const s = new Set(identified.map(c => c.attribution?.utm_source).filter(Boolean));
    return Array.from(s).sort();
  }, [identified]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return identified.filter(c => {
      if (srcFilter !== 'all' && c.attribution?.utm_source !== srcFilter) return false;
      if (!passesDate(c, dateRange, customRange, todayStr, timezone)) return false;
      if (q) return c.name?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q) || c.phone?.toLowerCase().includes(q);
      return true;
    });
  }, [identified, srcFilter, dateRange, customRange, todayStr, timezone, search]);

  const activeFilters = (srcFilter !== 'all' ? 1 : 0) + (dateRange !== 'all' ? 1 : 0);
  const clearFilters = () => { setSrc('all'); setDateRange('all'); setCustomRange(null); setSearch(''); };

  const handleExportPdf = async () => {
    if (exporting || error || !filtered.length) return;
    const toExport = selectedIds.size > 0
      ? filtered.filter(c => selectedIds.has(c.contact_id))
      : filtered;
    setExporting(true);
    try {
      const { fname, count } = await exportLeadsToPdf(toExport, dateLabel(dateRange, customRange), timezone);
      toast.success(`Saved ${fname}  (${count} lead${count !== 1 ? 's' : ''})`);
    } catch (e) {
      toast.error(`Export failed: ${e.message}`);
    } finally {
      setExporting(false);
    }
  };

  const exportCount = selectedIds.size > 0 ? selectedIds.size : filtered.length;

  // Leads — design: the prototype's Leads directory. Identified contacts (an email, phone or name) from the newest
  // 10,000 the API returns, with the live page's filters, PDF export and tracking script.
  const exportButton = (
        <button type="button" className="btn" onClick={handleExportPdf} disabled={exporting || !!error || filtered.length === 0}
          aria-label={`Export ${exportCount} ${selectedIds.size > 0 ? 'selected' : 'filtered'} people as PDF`} aria-busy={exporting || undefined}>
          {exporting ? <Loader2 size={15} className="sp-spin" /> : <FileText size={15} />} {exporting ? 'Exporting…' : 'Export PDF'}
          {exportCount > 0 && <span className="sp-button-count">{count(exportCount)}{selectedIds.size > 0 ? ' selected' : ''}</span>}
        </button>
  );
  const Wrap = embedded ? 'div' : 'section';
  return <>
    {!embedded && <PageIntro title="People" />}
    <Wrap className={embedded ? undefined : 'panel'} aria-label="Leads list">
      <div className="table-toolbar">
        <label className="search-input"><Search size={17} aria-hidden="true" /><span className="sr-only">Search leads</span>
          <input data-testid="leads-filter-search-input" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, email, phone…" /></label>
        <label className="select-wrap"><span className="sr-only">Filter by source</span>
          <select data-testid="leads-filter-source-select" value={srcFilter} onChange={e => setSrc(e.target.value)}>
            <option value="all">All sources</option>{sources.map(s => <option key={s} value={s}>{s}</option>)}
          </select><ChevronDown size={15} aria-hidden="true" /></label>
        <label className="select-wrap"><span className="sr-only">Date range</span>
          <select data-testid="leads-filter-date-range" value={dateRange} onChange={e => handleDateRangeChange(e.target.value)}>
            {DATE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.value === 'custom' && dateRange === 'custom' && customRange ? dateLabel('custom', customRange) : o.label}</option>)}
          </select><ChevronDown size={15} aria-hidden="true" /></label>
        <Popover open={calOpen} onOpenChange={open => { if (open) setDraftRange(customRange); setCalOpen(open); }}>
          <PopoverTrigger asChild>
            <button type="button" className="btn" data-active={(dateRange === 'custom' && !!customRange) || undefined}
              aria-label="Choose custom dates">
              <CalendarDays size={15} /> {dateRange === 'custom' && customRange ? dateLabel('custom', customRange) : 'Pick dates'}
            </button>
          </PopoverTrigger>
          <PopoverContent className="sp-popover w-auto p-0" align="start" aria-label="Choose dates">
            <Calendar mode="range" selected={draftRange} numberOfMonths={2} initialFocus onSelect={setDraftRange} />
            <div className="sp-popover-foot">
              <span>{draftRange?.from ? dateLabel('custom', draftRange) : 'Choose a day or date range'}</span>
              <span className="row">
                <button type="button" className="btn" onClick={() => setCalOpen(false)}>Cancel</button>
                <button type="button" className="btn primary" disabled={!draftRange?.from} onClick={() => {
                  setCustomRange({ from: draftRange.from, to: draftRange.to || draftRange.from });
                  setDateRange('custom'); setCalOpen(false);
                }}>Apply dates</button>
              </span>
            </div>
          </PopoverContent>
        </Popover>
        {(activeFilters > 0 || search) && (
          <button type="button" className="text-btn" onClick={clearFilters}>
            <X size={13} /> Clear filters
          </button>
        )}
        <span className="toolbar-end">
          {exportButton}
          <DirectoryRefresh onRefresh={onRefresh} loading={loading} label="Refresh people" />
          <span className="badge">{error ? 'Results unavailable' : `${count(filtered.length)} results`}</span>
        </span>
      </div>
      <ContactsTable
        contacts={filtered}
        loading={loading}
        initialLoad={initialLoad}
        error={error}
        onRetry={onRefresh}
        onSelectContact={onSelectContact}
        onCopyScript={handleCopyScript}
        onBulkDelete={onBulkDelete}
        hideSearch
        hasFilters={activeFilters > 0 || !!search}
        filterKey={JSON.stringify([search, srcFilter, dateRange, customRange])}
        onClearFilters={clearFilters}
        onSelectionChange={setSelectedIds}
      />
    </Wrap>
  </>;
}
